/**
 * Brand identity — single source of truth.
 *
 * Read by every UI surface that needs the brand name, support email, slug,
 * etc. Values come from env vars set by `site-factory new` (see .env.example for
 * the full list of NEXT_PUBLIC_BRAND_* keys).
 *
 * If you ever find a hardcoded brand string elsewhere in this codebase, replace
 * it with an import from here. That is a bug, not a feature.
 */

const required = (key: string, value: string | undefined): string => {
  if (value === undefined || value === "") {
    if (process.env.NODE_ENV === "production") {
      throw new Error(`Missing required brand env var: ${key}`);
    }
    return `<unset:${key}>`;
  }
  return value;
};

export const branding = {
  /** Display brand name, e.g. "AcmeGPU" */
  name: required("NEXT_PUBLIC_BRAND_NAME", process.env.NEXT_PUBLIC_BRAND_NAME),
  /** Slug used for the telegram site id etc. — lowercase, hyphenated */
  slug: required("NEXT_PUBLIC_BRAND_SLUG", process.env.NEXT_PUBLIC_BRAND_SLUG),
  /** Primary domain, no protocol, e.g. "acmegpu.com" */
  domain: required("NEXT_PUBLIC_BRAND_DOMAIN", process.env.NEXT_PUBLIC_BRAND_DOMAIN),
  /** Public-facing support email */
  supportEmail: required("NEXT_PUBLIC_BRAND_SUPPORT_EMAIL", process.env.NEXT_PUBLIC_BRAND_SUPPORT_EMAIL),
  /** Canonical app URL with protocol, e.g. "https://acmegpu.com" */
  appUrl: required("NEXT_PUBLIC_APP_URL", process.env.NEXT_PUBLIC_APP_URL),
} as const;

/** Derived: Telegram site identifier (override via TELEGRAM_SITE_ID if you need to). */
export const telegramSiteId = process.env.TELEGRAM_SITE_ID || branding.slug;
